<template>
  <div class="server-table">
    <div class="st-toolbar">
      <div v-if="tabs && tabs.length" class="st-tabs" role="tablist">
        <button v-for="t in tabs" :key="'t' + t.value" type="button" role="tab"
                :class="['st-tab', { active: String(t.value) === String(tab) }]"
                :aria-selected="String(t.value) === String(tab)" @click="setTab(t.value)">
          {{ t.label }}<span v-if="tabCount(t) !== null" class="st-count">{{ tabCount(t) }}</span>
        </button>
      </div>
      <form class="st-search" @submit.prevent="reload()">
        <input v-model.trim="search" type="search" class="form-control form-control-sm" :placeholder="searchPlaceholder">
        <button type="submit" class="btn btn-sm btn-primary">جستجو</button>
      </form>
      <slot name="toolbar"/>
    </div>
    <div class="st-meta text-muted small" v-if="total !== null">{{ total }} مورد</div>
    <CDataTableFixed
        :items="rows"
        :fields="fields"
        :items-per-page="20"
        hover
        :loading="loading"
        :has-more="hasMore"
        :load-error="error"
        @load-more="loadMore()"
        :noItemsView="{ noItems: emptyText, noResults: emptyText }"
    >
      <template v-for="(_, name) in $scopedSlots" #[name]="p">
        <slot :name="name" v-bind="p"/>
      </template>
    </CDataTableFixed>
  </div>
</template>

<script>
import axios from "@/api/http";
import { cacheGet, cacheKey, cacheSet, cacheDelPrefix } from '@/utils/listCache'

/**
 * Server-paged list: POSTs {page, limit: 20, search, [tabParam]: tab, ...params} to `url`
 * and appends rows when CDataTableFixed reaches its sentinel (infinite scroll, skeleton rows while loading).
 * First page is session-cached for instant revisit; only the table body skeletons while fetching.
 * Expects { error, items, total, has_more, counts? }.
 */
export default {
  name: 'ServerTable',
  props: {
    url: { type: String, required: true },
    fields: { type: Array, required: true },
    params: { type: Object, default: () => ({}) },
    tabs: { type: Array, default: () => [] },
    tabParam: { type: String, default: 'status' },
    defaultTab: { type: [String, Number], default: '' },
    mapRow: { type: Function, default: (r) => r },
    searchPlaceholder: { type: String, default: 'جستجو…' },
    emptyText: { type: String, default: 'موردی یافت نشد' },
    limit: { type: Number, default: 20 },
  },
  data() {
    return { rows: [], page: 0, hasMore: true, loading: false, error: false, seq: 0, total: null, counts: {}, search: '', tab: this.defaultTab }
  },
  watch: {
    params: { deep: true, handler() { this.reload() } },
    url() { this.reload() },
  },
  mounted() { this.reload() },
  methods: {
    pageCacheKey() {
      return cacheKey('st', this.url, this.tabParam, this.tab, this.search, JSON.stringify(this.params || {}))
    },
    tabCount(t) {
      if (t.value === '' || t.value === null || t.value === undefined) {
        const vals = Object.values(this.counts)
        return vals.length ? vals.reduce((a, b) => a + Number(b || 0), 0) : null
      }
      return Object.prototype.hasOwnProperty.call(this.counts, t.value) ? this.counts[t.value] : (Object.keys(this.counts).length ? 0 : null)
    },
    setTab(v) { if (String(v) !== String(this.tab)) { this.tab = v; this.reload() } },
    applyFirstPage(d, { append } = {}) {
      const add = (d.items || d.data || []).map(this.mapRow)
      this.rows = append ? this.rows.concat(add) : add
      this.page = d.page || 1
      this.hasMore = !!d.has_more
      this.total = d.total ?? null
      if (d.counts) this.counts = d.counts
    },
    reload() {
      this.seq += 1
      const cached = cacheGet(this.pageCacheKey())
      if (cached) {
        this.applyFirstPage(cached)
        this.loading = false
      } else {
        this.rows = []
        this.page = 0
        this.hasMore = true
        this.loading = false
      }
      this.error = false
      // always refresh first page from network
      this.page = 0
      this.hasMore = true
      this.loadMore({ replace: true })
    },
    bustCache() {
      cacheDelPrefix(cacheKey('st', this.url))
    },
    /** Replace one row in place (after a status change etc.) */
    patchRow(id, changes) {
      const i = this.rows.findIndex((r) => r.id === id)
      if (i >= 0) this.$set(this.rows, i, { ...this.rows[i], ...changes })
    },
    removeRow(id) {
      this.rows = this.rows.filter((r) => r.id !== id)
      if (this.total) this.total -= 1
      this.bustCache()
    },
    loadMore({ replace } = {}) {
      if (this.loading || (!replace && !this.hasMore)) return
      // skeleton only on empty table body; cached revisit refreshes quietly
      this.loading = replace ? !this.rows.length : true
      this.error = false
      const seq = this.seq
      const nextPage = replace ? 1 : this.page + 1
      const body = { ...this.params, page: nextPage, limit: this.limit, search: this.search }
      if (this.tabs.length && this.tab !== '' && this.tab !== null) body[this.tabParam] = this.tab
      axios.post(this.url, body, { show_pros: false }).then((res) => {
        if (seq !== this.seq) return
        const d = res.data || {}
        if (d.error) throw new Error(d.msg || 'error')
        if (replace || nextPage === 1) {
          this.applyFirstPage(d)
          cacheSet(this.pageCacheKey(), d)
        } else {
          const seen = new Set(this.rows.map((x) => x.id))
          const add = (d.items || d.data || []).map(this.mapRow).filter((x) => !seen.has(x.id))
          this.rows = this.rows.concat(add)
          this.page = d.page || this.page + 1
          this.hasMore = !!d.has_more
          this.total = d.total ?? this.total
          if (d.counts) this.counts = d.counts
        }
        this.$emit('loaded', d)
      }).catch((e) => {
        if (seq === this.seq) this.error = true
        console.log(e)
      }).finally(() => {
        if (seq === this.seq) this.loading = false
      })
    },
  },
}
</script>

<style scoped>
.st-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.st-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.st-tab { border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 4px 14px; font-size: 13px; color: #374151; cursor: pointer; }
.st-tab.active { background: var(--accent, #2563eb); border-color: var(--accent, #2563eb); color: #fff; }
.st-count { margin-inline-start: 6px; font-size: 11px; opacity: .8; }
.st-search { display: flex; gap: 6px; min-width: 240px; }
.st-meta { margin-bottom: 6px; }
</style>
